/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================
* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)
Coded by www.creative-tim.com
 =========================================================
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useContext, useEffect } from 'react'

// @mui core components
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Fade,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import HelpIcon from '@mui/icons-material/Help'

// Settings page components
import FormField from 'components/FormField'

// Data
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

// Debounce
import { useFormDebounce } from 'hooks/useFormDebounce'
import styled, { keyframes } from 'styled-components'
import LoopIcon from '@mui/icons-material/Loop'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import OnboardingMultiSelectTile from 'components/OnboardingMultiSelectTile'

import SoftTypography from 'components/SoftTypography'
import PrivateLifeInsurance from 'admin/components/PrivateLifeInsurance'

const rotatingIcon = keyframes`
  0% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(0deg);
  }
`
const colorChangeIcon = keyframes`
  0% {
    color: #66d432;
  }
  50% {
    color: #66d432;
  }
  100% {
    color: #344767;
  }
`
const ChangesSavedIcon = styled(CheckCircleOutlineIcon)`
  &.saved {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 2s ${colorChangeIcon} ease-out;
  }
`
const SavingIcon = styled(LoopIcon)`
  &.loading {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 1.5s ${rotatingIcon} linear infinite;
  }
`

function LifeInsurance() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your life insurance details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }
  const showFEGLI = intakeState?.fegli
  const lifeInsuranceOther = intakeState?.lifeInsuranceOther

  useEffect(() => {
    // console.log(intakeState)
  }, [intakeState])

  return (
    <Card
      sx={{
        textAlign: 'left',
        boxShadow: 0,
        border: '1px solid #ddd',
        borderRadius: 2,
      }}
    >
      <Box px={3} pt={3} display="flex" justifyContent="space-between">
        <Typography variant="h3" fontSize={{ xs: '24px', md: '30px' }}>
          FEGLI & Life Insurance
        </Typography>

        <Button
          variant="gradient"
          color="dark"
          sx={{ height: '100%' }}
          onClick={onSubmit}
          startIcon={
            loading ? (
              <>
                <SavingIcon fontSize="medium" className={'loading'} />
              </>
            ) : (
              <>
                <ChangesSavedIcon fontSize="medium" className={'saved'} />
              </>
            )
          }
        >
          {loading ? `updating...` : 'up to date'}
        </Button>
      </Box>
      <Box component="form" pb={3} px={3}>
        <Grid container columnSpacing={3} rowSpacing={2}>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'fegli',
              label: 'Are you currently enrolled in FEGLI coverage?',
              placeholder: '',
              inputType: 'select',
              value: intakeState.fegli,
              onChange: updateIntakeStateAndSubmit((fegli) =>
                updateIntakeStateAtKey({ fegli })
              ),
              options: selectData.yesNo,
            })}
          </Grid>
          {showFEGLI && (
            <>
              <Grid item xs={12} sm={12}>
                <SoftTypography
                  component="label"
                  variant="body2"
                  fontWeight="regular"
                  textTransform="capitalize"
                >
                  Do you have any optional FEGLI coverage?
                </SoftTypography>

                {/* this currently does not debounce properly, but does update the intakeState */}
                <OnboardingMultiSelectTile
                  header={false}
                  helpText={'test'}
                  subHeader={false}
                  tileSize={{ xs: '4', sm: '4', md: '4', lg: '4' }}
                  questionArray={[
                    {
                      id: 'fegliOptionA',
                      optionLabel: 'Option A',
                    },
                    {
                      id: 'fegliOptionB',
                      optionLabel: 'Option B',
                    },
                    {
                      id: 'fegliOptionC',
                      optionLabel: 'Option C',
                    },
                  ]}
                />
              </Grid>

              {intakeState.fegliOptionB && (
                <Grid item xs={12} sm={intakeState.fegliOptionC ? 6 : 12}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'fegliOptionBMultiples',
                    label: 'How many multiples of option B do you have?',
                    placeholder: '0',
                    inputType: 'int',
                    value: intakeState.fegliOptionBMultiples,
                    onChange: updateIntakeStateAndSubmit(
                      (fegliOptionBMultiples) =>
                        updateIntakeStateAtKey({ fegliOptionBMultiples })
                    ),
                  })}
                </Grid>
              )}
              {intakeState.fegliOptionC && (
                <Grid item xs={12} sm={intakeState.fegliOptionB ? 6 : 12}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'fegliOptionCMultiples',
                    label: 'How many multiples of option C do you have?',
                    placeholder: '0',
                    inputType: 'int',
                    value: intakeState.fegliOptionCMultiples,
                    onChange: updateIntakeStateAndSubmit(
                      (fegliOptionCMultiples) =>
                        updateIntakeStateAtKey({ fegliOptionCMultiples })
                    ),
                  })}
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12} mt={2}>
            <Typography variant="h5">Private Life Insurance</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'lifeInsuranceOther',
              label: 'Do you have any private life insurance?',
              placeholder: '',
              inputType: 'select',
              value: intakeState.lifeInsuranceOther,
              onChange: updateIntakeStateAndSubmit((lifeInsuranceOther) =>
                updateIntakeStateAtKey({ lifeInsuranceOther })
              ),
              options: selectData.yesNo,
            })}
          </Grid>
          {lifeInsuranceOther && (
            <Grid item xs={12}>
              <PrivateLifeInsurance
                numProducts={
                  intakeState?.privateLifeInsurance
                    ? intakeState.privateLifeInsurance.length
                    : 0
                }
                initialProducts={intakeState?.privateLifeInsurance || []}
                updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
                updateIntakeStateAtKey={updateIntakeStateAtKey}
              />
            </Grid>
            // <>
            //   <Grid item xs={12} sm={6}>
            //     {renderMuiFormField({
            //       autoFocus: false,
            //       id: 'lifeInsuranceOtherProvider',
            //       label: 'Who is your provider?',
            //       placeholder: '',
            //       inputType: 'select',
            //       value: intakeState.lifeInsuranceOtherProvider,
            //       onChange: updateIntakeStateAndSubmit(
            //         (lifeInsuranceOtherProvider) =>
            //           updateIntakeStateAtKey({ lifeInsuranceOtherProvider })
            //       ),
            //       options: selectData.privateLifeInsuranceProviders,
            //     })}
            //   </Grid>
            //   <Grid item xs={12} sm={6} md={4}>
            //     {renderMuiFormField({
            //       autoFocus: false,
            //       id: 'lifeInsuranceOtherCoverageAmount',
            //       label: 'Coverage Amount',
            //       placeholder: '$250,000.00',
            //       inputType: 'currencyFloat',
            //       value: intakeState.lifeInsuranceOtherCoverageAmount,
            //       onChange: updateIntakeStateAndSubmit(
            //         (lifeInsuranceOtherCoverageAmount) =>
            //           updateIntakeStateAtKey({
            //             lifeInsuranceOtherCoverageAmount,
            //           })
            //       ),
            //     })}
            //   </Grid>
            //   <Grid item xs={12} sm={6} md={4}>
            //     {renderMuiFormField({
            //       autoFocus: false,
            //       id: 'lifeInsuranceOtherPremium',
            //       label: 'Monthly Premium',
            //       placeholder: '$250.00',
            //       inputType: 'currencyFloat',
            //       value: intakeState.lifeInsuranceOtherPremium,
            //       onChange: updateIntakeStateAndSubmit(
            //         (lifeInsuranceOtherPremium) =>
            //           updateIntakeStateAtKey({
            //             lifeInsuranceOtherPremium,
            //           })
            //       ),
            //     })}
            //   </Grid>
            //   <Grid item xs={12} sm={6} md={4}>
            //     {renderMuiFormField({
            //       autoFocus: false,
            //       id: 'lifeInsuranceOtherType',
            //       label: 'Coverage Type',
            //       placeholder: 'Type',
            //       inputType: 'select',
            //       value: intakeState.lifeInsuranceOtherType,
            //       onChange: updateIntakeStateAndSubmit(
            //         (lifeInsuranceOtherType) =>
            //           updateIntakeStateAtKey({
            //             lifeInsuranceOtherType,
            //           })
            //       ),
            //       options: selectData.privateLifeInsuranceTypes,
            //     })}
            //   </Grid>
            // </>
          )}
        </Grid>
      </Box>
    </Card>
  )
}

export default LifeInsurance
